import { useState } from 'react';
import { ArrowRight, Send, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { contactReasons } from '@/data/mock';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const Reveal = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    organization: '',
    title: '',
    reason: '',
    message: '',
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <section className="bg-[#0B1D33] pt-32 pb-20 lg:pt-36 lg:pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-[#3B82A0] text-[11px] font-medium uppercase tracking-[0.3em] mb-4">
            Get in Touch
          </p>
          <h1 className="text-3xl md:text-4xl font-light text-white mb-5 tracking-tight">
            Engage Our Experts
          </h1>
          <p className="text-slate-300/80 text-lg max-w-2xl leading-relaxed">
            Start a conversation about your cybersecurity challenges. Our advisory team responds
            within one business day.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="bg-[#F8FAFC] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-4">
              <Reveal>
                <div>
                  <h2 className="text-xl font-semibold text-[#0B1D33] mb-6">
                    How We Can Help
                  </h2>
                  <p className="text-slate-500 leading-relaxed mb-8">
                    Whether you're navigating a compliance initiative, building a security program,
                    or seeking strategic advisory support, we're here to help you define the path forward.
                  </p>

                  <div className="space-y-8">
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-2">
                        Email
                      </h4>
                      <p className="text-sm text-[#0B1D33] font-medium">info@cisodynamics.com</p>
                    </div>
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-2">
                        Location
                      </h4>
                      <p className="text-sm text-[#0B1D33] font-medium">Washington, D.C.</p>
                    </div>
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-2">
                        Response Time
                      </h4>
                      <p className="text-sm text-[#0B1D33] font-medium">Within 1 business day</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>

            {/* Form */}
            <div className="lg:col-span-8">
              <Reveal delay={100}>
                {submitted ? (
                  <div className="bg-white rounded-lg border border-slate-200/80 p-10 lg:p-14 text-center">
                    <div className="w-14 h-14 rounded-full bg-[#3B82A0]/10 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="h-7 w-7 text-[#3B82A0]" />
                    </div>
                    <h3 className="text-xl font-light text-[#0B1D33] mb-3 tracking-tight">
                      Thank You
                    </h3>
                    <p className="text-slate-500 max-w-md mx-auto leading-relaxed">
                      Your inquiry has been received. A member of our advisory team will respond
                      within one business day.
                    </p>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    className="bg-white rounded-lg border border-slate-200/80 p-8 lg:p-10"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                      <div className="space-y-2">
                        <Label htmlFor="firstName" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                          First Name
                        </Label>
                        <Input
                          id="firstName"
                          required
                          value={formData.firstName}
                          onChange={(e) => handleChange('firstName', e.target.value)}
                          className="h-11 border-slate-200 focus:border-[#3B82A0] focus:ring-[#3B82A0]/20"
                          placeholder="First name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                          Last Name
                        </Label>
                        <Input
                          id="lastName"
                          required
                          value={formData.lastName}
                          onChange={(e) => handleChange('lastName', e.target.value)}
                          className="h-11 border-slate-200 focus:border-[#3B82A0] focus:ring-[#3B82A0]/20"
                          placeholder="Last name"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                          Email
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          className="h-11 border-slate-200 focus:border-[#3B82A0] focus:ring-[#3B82A0]/20"
                          placeholder="you@company.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="organization" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                          Organization
                        </Label>
                        <Input
                          id="organization"
                          value={formData.organization}
                          onChange={(e) => handleChange('organization', e.target.value)}
                          className="h-11 border-slate-200 focus:border-[#3B82A0] focus:ring-[#3B82A0]/20"
                          placeholder="Your organization"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                      <div className="space-y-2">
                        <Label htmlFor="title" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                          Title
                        </Label>
                        <Input
                          id="title"
                          value={formData.title}
                          onChange={(e) => handleChange('title', e.target.value)}
                          className="h-11 border-slate-200 focus:border-[#3B82A0] focus:ring-[#3B82A0]/20"
                          placeholder="Your title"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                          Area of Interest
                        </Label>
                        <Select
                          value={formData.reason}
                          onValueChange={(val) => handleChange('reason', val)}
                        >
                          <SelectTrigger className="h-11 border-slate-200 focus:border-[#3B82A0] focus:ring-[#3B82A0]/20">
                            <SelectValue placeholder="Select a topic" />
                          </SelectTrigger>
                          <SelectContent>
                            {contactReasons.map((reason) => (
                              <SelectItem key={reason} value={reason}>
                                {reason}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2 mb-8">
                      <Label htmlFor="message" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Message
                      </Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        className="min-h-[120px] border-slate-200 focus:border-[#3B82A0] focus:ring-[#3B82A0]/20 resize-none"
                        placeholder="Tell us about your cybersecurity challenges and objectives..."
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={submitting}
                      className="bg-[#0B1D33] hover:bg-[#162D4A] text-white px-8 h-11 text-sm font-medium rounded transition-colors duration-200 disabled:opacity-70"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit Inquiry
                          <Send className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                    {error && (
                      <p className="text-sm text-red-500 mt-3">{error}</p>
                    )}
                  </form>
                )}
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
